"use client";

import { LATEST_BLOGS_2_WIDGET_DEFAULTS } from "@/constants/admin/fields/latestBlogs/latest_blogs_2";
// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";
import { publicAxios } from "@/lib/axios";
import { cn, formatSectionTitle } from "@/lib/utils";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import {
  type CSSProperties,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

/** Match BlogList1 card date styling defaults. */
const BLOG_CARD_DATE_TEXT = "#373737";

/** Number of popular blogs to request per page from the filter API. */
const FETCH_LIMIT = 12;

/** Carousel card geometry (px) — drives the smooth translate-based slide. */
const CARD_WIDTH = 328;
const CARD_GAP = 16;
const CARD_STEP = CARD_WIDTH + CARD_GAP; // distance the track moves per card

type BlogPost = {
  id: number;
  path: string;
  blog_banner_image: string | null;
  blog_title: string | null;
  blog_category_ids?: number[] | null;
  is_popular?: boolean;
  is_featured?: boolean;
  created_at?: string | null;
};

type BlogCategory = {
  id: number;
  name?: string | null;
  background_image_url?: string | null;
  icon_url?: string | null;
  is_default_category?: boolean | number | string;
};

/** "12 Feb 2026" */
function formatBlogDate(value?: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.toLocaleDateString("en-US", { month: "short" });
  return `${d.getDate()} ${month} ${d.getFullYear()}`;
}

export default function LatestBlogs2({
  data,
  isCmsPreview = false,
}: {
  data: any;
  isCmsPreview?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  const sectionData = { ...LATEST_BLOGS_2_WIDGET_DEFAULTS, ...((data as any)?.data ?? data) };

  /** Leftmost visible card index; arrows move by one card. */
  const [startIndex, setStartIndex] = useState(0);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [total, setTotal] = useState(0);
  /** New Blogs (is_latest, desc) — infinite-scroll list. */
  const [newPosts, setNewPosts] = useState<BlogPost[]>([]);
  const [newTotal, setNewTotal] = useState(0);
  const newLoadingRef = useRef(false);
  /** Featured cards (is_featured by default; filtered by selected category) + load-more. */
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [featuredPosts, setFeaturedPosts] = useState<BlogPost[]>([]);
  const [featuredTotal, setFeaturedTotal] = useState(0);
  const featuredLoadingRef = useRef(false);
  // 1024 (not 1100) so the JS layout gate aligns with the `min-[1024px]:` CSS
  // utilities — at >=1024 both pick the desktop layout, matching the rest of the page.
  const isMobileHook = useIsMobile(1024);
  const isMobileLayout = isMobileHook === true;

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "latest_blogs_section");
  //   }
  // }, []);

  const {
    title,
    popular_section_title,
    new_blogs_section_title,
    new_blogs_hero_image,
    new_blogs_hero_url,
    new_blogs_hero_alt,
    new_blogs_hero_title,
    new_blogs_hero_subtitle,
    title_text_color,
    section_background_color,
    read_more_label,
    read_more_text_color,
    nav_button_active_bg,
    nav_button_inactive_bg,
    nav_icon_color,
    university_name,
    university_logo,
    university_logo_alt,
    university_id = null,
    popular_blogs,
    new_blogs,
    categories,
    featured_blogs,
  } = sectionData || {};

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  /** Popular blogs from the filter API (is_popular=true); seeded in CMS preview. */
  useEffect(() => {
    if (isCmsPreview) {
      const seed = Array.isArray(popular_blogs?.items) ? popular_blogs.items : [];
      setPosts(seed);
      setTotal(typeof popular_blogs?.total === "number" ? popular_blogs.total : seed.length);
      return;
    }
    if (university_id == null) return;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await publicAxios.get("/page/blog-posts/filter", {
          params: {
            university_id,
            is_popular: true,
            order: "asc",
            offset: 0,
            limit: FETCH_LIMIT,
          },
        });
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        setPosts(Array.isArray(payload?.items) ? payload.items : []);
        setTotal(typeof payload?.total === "number" ? payload.total : 0);
      } catch (e) {
        console.error(e);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isCmsPreview, university_id, popular_blogs]);

  const visibleSlots = isMobileLayout ? 1 : Math.min(3, posts.length);
  const maxStart = Math.max(0, posts.length - visibleSlots);
  /** Width of the visible card strip — heading + carousel share this centered column. */
  const carouselWidth =
    visibleSlots * CARD_WIDTH + Math.max(0, visibleSlots - 1) * CARD_GAP;

  useEffect(() => {
    setStartIndex((i) => Math.min(i, maxStart));
  }, [maxStart, posts.length, visibleSlots]);

  const fetchMore = useCallback(async () => {
    if (isCmsPreview || university_id == null) return;
    if (posts.length >= total) return;
    try {
      const res = await publicAxios.get("/page/blog-posts/filter", {
        params: {
          university_id,
          is_popular: true,
          order: "asc",
          offset: posts.length,
          limit: FETCH_LIMIT,
        },
      });
      const payload = res.data?.data ?? res.data;
      const next = Array.isArray(payload?.items) ? payload.items : [];
      setPosts((prev) => [...prev, ...next]);
      if (typeof payload?.total === "number") setTotal(payload.total);
    } catch (e) {
      console.error(e);
    }
  }, [isCmsPreview, university_id, posts.length, total]);

  /** New Blogs: all blogs newest-first (is_latest); seeded in CMS preview. */
  useEffect(() => {
    if (isCmsPreview) {
      const seed = Array.isArray(new_blogs?.items) ? new_blogs.items : [];
      setNewPosts(seed);
      setNewTotal(typeof new_blogs?.total === "number" ? new_blogs.total : seed.length);
      return;
    }
    if (university_id == null) return;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await publicAxios.get("/page/blog-posts/filter", {
          params: {
            university_id,
            is_latest: true,
            order: "desc",
            offset: 0,
            limit: FETCH_LIMIT,
          },
        });
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        setNewPosts(Array.isArray(payload?.items) ? payload.items : []);
        setNewTotal(typeof payload?.total === "number" ? payload.total : 0);
      } catch (e) {
        console.error(e);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isCmsPreview, university_id, new_blogs]);

  const fetchMoreNew = useCallback(async () => {
    if (isCmsPreview || university_id == null) return;
    if (newLoadingRef.current) return;
    if (newPosts.length >= newTotal && newTotal > 0) return;
    newLoadingRef.current = true;
    try {
      const res = await publicAxios.get("/page/blog-posts/filter", {
        params: {
          university_id,
          is_latest: true,
          order: "desc",
          offset: newPosts.length,
          limit: FETCH_LIMIT,
        },
      });
      const payload = res.data?.data ?? res.data;
      const next = Array.isArray(payload?.items) ? payload.items : [];
      setNewPosts((prev) => [...prev, ...next]);
      if (typeof payload?.total === "number") setNewTotal(payload.total);
    } catch (e) {
      console.error(e);
    } finally {
      newLoadingRef.current = false;
    }
  }, [isCmsPreview, university_id, newPosts.length, newTotal]);

  /** Infinite scroll: load the next page when the list nears its bottom. */
  const onNewBlogsScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) {
        void fetchMoreNew();
      }
    },
    [fetchMoreNew],
  );

  // --- Categories + Featured cards ---
  const categoriesList: BlogCategory[] = Array.isArray(categories) ? categories : [];
  const defaultCategoryRow = categoriesList.find(
    (c) =>
      c.is_default_category === true ||
      c.is_default_category === 1 ||
      c.is_default_category === "1",
  );
  const defaultCategoryId = defaultCategoryRow?.id ?? null;
  /** Omit category filter for the default chip / no selection (→ featured cards). */
  const categoryIdForQuery =
    selectedCategoryId != null && selectedCategoryId !== defaultCategoryId
      ? selectedCategoryId
      : null;

  const featuredParams = useCallback(
    (offset: number) =>
      categoryIdForQuery != null
        ? { university_id, category_id: categoryIdForQuery, order: "desc", offset, limit: FETCH_LIMIT }
        : { university_id, is_featured: true, order: "asc", offset, limit: FETCH_LIMIT },
    [categoryIdForQuery, university_id],
  );

  /** Featured cards: is_featured by default, or the selected category; seeded in CMS preview. */
  useEffect(() => {
    if (isCmsPreview) {
      const seed = Array.isArray(featured_blogs?.items) ? featured_blogs.items : [];
      setFeaturedPosts(seed);
      setFeaturedTotal(typeof featured_blogs?.total === "number" ? featured_blogs.total : seed.length);
      return;
    }
    if (university_id == null) return;

    let cancelled = false;
    const load = async () => {
      try {
        const res = await publicAxios.get("/page/blog-posts/filter", {
          params: featuredParams(0),
        });
        if (cancelled) return;
        const payload = res.data?.data ?? res.data;
        setFeaturedPosts(Array.isArray(payload?.items) ? payload.items : []);
        setFeaturedTotal(typeof payload?.total === "number" ? payload.total : 0);
      } catch (e) {
        console.error(e);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [isCmsPreview, university_id, featuredParams, featured_blogs]);

  const loadMoreFeatured = useCallback(async () => {
    if (isCmsPreview || university_id == null) return;
    if (featuredLoadingRef.current) return;
    if (featuredPosts.length >= featuredTotal && featuredTotal > 0) return;
    featuredLoadingRef.current = true;
    try {
      const res = await publicAxios.get("/page/blog-posts/filter", {
        params: featuredParams(featuredPosts.length),
      });
      const payload = res.data?.data ?? res.data;
      const next = Array.isArray(payload?.items) ? payload.items : [];
      setFeaturedPosts((prev) => [...prev, ...next]);
      if (typeof payload?.total === "number") setFeaturedTotal(payload.total);
    } catch (e) {
      console.error(e);
    } finally {
      featuredLoadingRef.current = false;
    }
  }, [isCmsPreview, university_id, featuredParams, featuredPosts.length, featuredTotal]);

  const toggleCategory = useCallback((id: number) => {
    setSelectedCategoryId((prev) => (prev === id ? null : id));
  }, []);

  const goPrev = useCallback(() => {
    setStartIndex((i) => Math.max(0, i - 1));
  }, []);

  const goNext = useCallback(() => {
    setStartIndex((i) => {
      const nextIndex = Math.min(maxStart, i + 1);
      // Reached the end of the loaded set but more exist — fetch the next page.
      if (nextIndex === maxStart && posts.length < total) void fetchMore();
      return nextIndex;
    });
  }, [maxStart, posts.length, total, fetchMore]);

  const showNav = posts.length > visibleSlots || posts.length < total;
  const canPrev = startIndex > 0;
  const canNext = startIndex < maxStart || posts.length < total;
  const prevBg = canPrev ? nav_button_active_bg : nav_button_inactive_bg;
  const nextBg = canNext ? nav_button_active_bg : nav_button_inactive_bg;
  /** Disabled arrow: show the active-button color as the icon (on the inactive bg). */
  const prevIconColor = canPrev ? nav_icon_color : nav_button_active_bg;
  const nextIconColor = canNext ? nav_icon_color : nav_button_active_bg;

  const readMoreColor = read_more_text_color || "#DB5803";

  /** Logo + university name badge (overlay on image, or inline below it). */
  const universityBadge = (variant: "overlay" | "inline") => {
    if (!university_logo && !university_name) return null;
    const textColor = variant === "overlay" ? "#FFFFFF" : "#101112";
    const logoSize = variant === "overlay" ? 16 : 20;
    return (
      <span className="inline-flex items-center gap-1.5">
        {university_logo ? (
          <span
            className="relative shrink-0"
            style={{ width: logoSize, height: logoSize }}
          >
            <Image
              src={university_logo}
              alt={university_logo_alt || university_name || ""}
              fill
              className="object-contain"
              sizes="20px"
            />
          </span>
        ) : null}
        {university_name ? (
          <span
            className={cn(
              "leading-tight",
              variant === "overlay"
                ? "text-[10px] font-semibold"
                : "text-[16px] font-normal [font-family:ui-sans-serif,system-ui,sans-serif]",
            )}
            style={{ color: textColor }}
          >
            {university_name}
          </span>
        ) : null}
      </span>
    );
  };

  const newsCard = (post: BlogPost) => {
    const date = formatBlogDate(post.created_at);
    const href = (post.path || "").trim() || "#";
    return (
      <Link
        key={post.id}
        href={href}
        className={cn(
          "block w-[328px] shrink-0",
          "[font-family:var(--font-poppins),Poppins,sans-serif]",
        )}
      >
        <div className="flex h-full cursor-pointer flex-col gap-5">
          <div className="relative w-full overflow-hidden rounded-lg">
            <div className="relative aspect-[328/178] w-full">
              {post.blog_banner_image ? (
                <Image
                  src={post.blog_banner_image}
                  alt={post.blog_title || ""}
                  fill
                  className="object-cover"
                  sizes="(max-width: 1100px) 90vw, 328px"
                />
              ) : (
                <div className="absolute inset-0 bg-neutral-200" aria-hidden />
              )}
            </div>
            <div className="absolute left-3 top-3">
              {universityBadge("overlay")}
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-between gap-[14px]">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between pr-2">
                {universityBadge("inline")}
                {date ? (
                  <span className="inline-flex items-center gap-1.5">
                    <Image
                      src="/svg/calendar.svg"
                      alt=""
                      width={18}
                      height={18}
                      className="shrink-0"
                      aria-hidden
                    />
                    <span
                      className="whitespace-nowrap text-[12px] font-medium leading-tight [font-family:var(--font-poppins),Poppins,sans-serif]"
                      style={{ color: BLOG_CARD_DATE_TEXT }}
                    >
                      {date}
                    </span>
                  </span>
                ) : null}
              </div>

              <h3
                className="line-clamp-3 text-left text-[16px] font-semibold leading-snug tracking-0 [font-family:var(--font-poppins),Poppins,sans-serif]"
                style={{ color: "#101112" }}
              >
                {post.blog_title || ""}
              </h3>
            </div>

            <span
              className="inline-flex items-center gap-1 text-[16px] font-semibold leading-[22px]"
              style={{ color: readMoreColor }}
            >
              {read_more_label || "Read more"}
              <ArrowUpRight width={22} height={22} aria-hidden />
            </span>
          </div>
        </div>
      </Link>
    );
  };

  /**
   * Mobile-only popular card: full-width image with a white info box
   * overlapping its lower edge (university badge + date + title). Distinct
   * from the desktop `newsCard` so the carousel arrows can flank it.
   */
  const mobilePopularCard = (post: BlogPost) => {
    const date = formatBlogDate(post.created_at);
    const href = (post.path || "").trim() || "#";
    return (
      <Link
        key={post.id}
        href={href}
        className={cn(
          "relative flex w-[300px] max-w-full flex-col items-center",
          "[font-family:var(--font-poppins),Poppins,sans-serif]",
        )}
      >
        <div className="absolute left-0 top-0 h-[176px] w-full overflow-hidden rounded-xl">
          {post.blog_banner_image ? (
            <Image
              src={post.blog_banner_image}
              alt={post.blog_title || ""}
              fill
              className="object-cover"
              sizes="300px"
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-200" aria-hidden />
          )}
        </div>

        <div className="relative mt-[118px] flex w-[280px] max-w-[92%] flex-col gap-2 rounded-xl bg-[#FFFFFF] p-4 shadow-[0_6px_24px_rgba(0,0,0,0.12)]">
          {universityBadge("inline")}
          {date ? (
            <span className="inline-flex items-center gap-1.5">
              <Image
                src="/svg/calendar.svg"
                alt=""
                width={18}
                height={18}
                className="shrink-0"
                aria-hidden
              />
              <span
                className="whitespace-nowrap text-[13px] font-medium leading-tight"
                style={{ color: BLOG_CARD_DATE_TEXT }}
              >
                {date}
              </span>
            </span>
          ) : null}
          <h3
            className="line-clamp-3 text-left text-[18px] font-bold leading-snug tracking-0"
            style={{ color: "#101112" }}
          >
            {post.blog_title || ""}
          </h3>
        </div>
      </Link>
    );
  };

  /** Compact horizontal card for the New Blogs list. */
  const newBlogCard = (post: BlogPost) => {
    const href = (post.path || "").trim() || "#";
    return (
      <Link
        key={post.id}
        href={href}
        className="flex items-center gap-4 [font-family:var(--font-poppins),Poppins,sans-serif]"
      >
        <div className="h-[90px] w-[104px] shrink-0 overflow-hidden rounded-[4px] min-[1024px]:h-[88px] min-[1024px]:w-[92px]">
          {post.blog_banner_image ? (
            <Image
              src={post.blog_banner_image}
              alt={post.blog_title || ""}
              width={104}
              height={90}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-neutral-200" aria-hidden />
          )}
        </div>
        <div className="flex flex-1 flex-col gap-2">
          <h3
            className="line-clamp-3 text-[16px] font-semibold leading-snug [font-family:var(--font-poppins),Poppins,sans-serif]"
            style={{ color: "#171719" }}
          >
            {post.blog_title || ""}
          </h3>
          <span
            className="inline-flex items-center gap-1 text-[14px] font-semibold leading-[22px]"
            style={{ color: readMoreColor }}
          >
            {read_more_label || "Read more"}
            <ArrowUpRight width={20} height={20} aria-hidden />
          </span>
        </div>
      </Link>
    );
  };

  /** Large featured card — image with title overlaid at the bottom. */
  const featuredCard = (post: BlogPost) => {
    const href = (post.path || "").trim() || "#";
    return (
      <Link key={post.id} href={href} className="block">
        <div className="relative h-[258px] w-[330px] max-w-full overflow-hidden bg-gray-100">
          {post.blog_banner_image ? (
            <Image
              src={post.blog_banner_image}
              alt={post.blog_title || ""}
              fill
              className="object-cover"
              sizes="330px"
            />
          ) : (
            <div className="absolute inset-0 bg-neutral-200" aria-hidden />
          )}
          <p className="absolute bottom-6 left-6 right-7 line-clamp-3 text-[16px] leading-snug text-[#FFFFFF] [font-family:var(--font-poppins),Poppins,sans-serif]">
            {post.blog_title || ""}
          </p>
        </div>
      </Link>
    );
  };

  if (!sectionData || !isActive) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn(
        "flex w-full flex-col items-center justify-center gap-6 px-spacing-8 pt-[42px] pb-8 min-[640px]:gap-6 min-[640px]:px-[36px] min-[640px]:pb-[42px] min-[768px]:px-[44px] min-[768px]:pb-[52px] min-[1024px]:px-[52px] min-[1024px]:pb-[60px] min-[1280px]:px-[64px] min-[1280px]:pb-[72px]",
      )}
      style={{ backgroundColor: section_background_color }}
    >
      <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-10">
        {/* Main title — supports {highlighted} text */}
        {title ? (
          <h1
            className="pt-[25px] text-center text-[28px] font-bold leading-tight min-[768px]:text-[36px] [font-family:var(--font-poppins),Poppins,sans-serif]"
            style={{ color: title_text_color }}
            dangerouslySetInnerHTML={{
              __html: formatSectionTitle(title, "text-primary"),
            }}
          />
        ) : null}

        {/* Popular blogs section */}
        {posts.length > 0 ? (
          isMobileLayout ? (
            /* Mobile: centered card with arrows flanking it left/right. */
            <div className="flex w-full flex-col gap-6">
              {popular_section_title ? (
                <h2
                  className="text-center text-[22px] font-semibold leading-tight [font-family:var(--font-poppins),Poppins,sans-serif]"
                  style={{ color: title_text_color }}
                  dangerouslySetInnerHTML={{
                    __html: formatSectionTitle(popular_section_title, "text-primary"),
                  }}
                />
              ) : null}

              <div className="relative mx-auto w-full max-w-[360px]">
                <div className="w-full overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out will-change-transform"
                    style={{ transform: `translateX(-${startIndex * 100}%)` }}
                  >
                    {posts.map((post) => (
                      <div
                        key={post.id}
                        className="flex w-full shrink-0 justify-center"
                      >
                        {mobilePopularCard(post)}
                      </div>
                    ))}
                  </div>
                </div>

                {showNav ? (
                  <>
                    <button
                      type="button"
                      aria-label="Previous blogs"
                      disabled={!canPrev}
                      onClick={goPrev}
                      className={cn(
                        "absolute left-0 top-[88px] z-20 flex size-10 shrink-0 -translate-y-1/2 items-center justify-center rounded-full shadow-md transition-opacity",
                        !canPrev && "cursor-not-allowed opacity-70",
                      )}
                      style={{ backgroundColor: prevBg, color: prevIconColor }}
                    >
                      <ChevronLeft className="size-6" strokeWidth={2.25} aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label="Next blogs"
                      disabled={!canNext}
                      onClick={goNext}
                      className={cn(
                        "absolute right-0 top-[88px] z-20 flex size-10 shrink-0 -translate-y-1/2 items-center justify-center rounded-full shadow-md transition-opacity",
                        !canNext && "cursor-not-allowed opacity-70",
                      )}
                      style={{ backgroundColor: nextBg, color: nextIconColor }}
                    >
                      <ChevronRight className="size-6" strokeWidth={2.25} aria-hidden />
                    </button>
                  </>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex w-full flex-col gap-6">
              <div
                className="mx-auto flex w-full flex-col gap-6"
                style={{ width: carouselWidth, maxWidth: "100%" }}
              >
                {popular_section_title ? (
                  <h2
                    className="text-left text-[22px] font-semibold leading-tight min-[768px]:text-[28px] [font-family:var(--font-poppins),Poppins,sans-serif]"
                    style={{ color: title_text_color }}
                    dangerouslySetInnerHTML={{
                      __html: formatSectionTitle(popular_section_title, "text-primary"),
                    }}
                  />
                ) : null}

                <div className="w-full overflow-hidden">
                  <div
                    className="flex transition-transform duration-500 ease-out will-change-transform"
                    style={{
                      gap: CARD_GAP,
                      transform: `translateX(-${startIndex * CARD_STEP}px)`,
                    }}
                  >
                    {posts.map((post) => newsCard(post))}
                  </div>
                </div>
              </div>

              {showNav ? (
                <div className="mt-2 flex justify-center gap-6">
                  <button
                    type="button"
                    aria-label="Previous blogs"
                    disabled={!canPrev}
                    onClick={goPrev}
                    className={cn(
                      "flex size-12 shrink-0 items-center justify-center rounded-full transition-opacity",
                      !canPrev && "cursor-not-allowed opacity-70",
                    )}
                    style={{ backgroundColor: prevBg, color: prevIconColor }}
                  >
                    <ChevronLeft
                      className="size-7"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </button>
                  <button
                    type="button"
                    aria-label="Next blogs"
                    disabled={!canNext}
                    onClick={goNext}
                    className={cn(
                      "flex size-12 shrink-0 items-center justify-center rounded-full transition-opacity",
                      !canNext && "cursor-not-allowed opacity-70",
                    )}
                    style={{ backgroundColor: nextBg, color: nextIconColor }}
                  >
                    <ChevronRight
                      className="size-7"
                      strokeWidth={2.25}
                      aria-hidden
                    />
                  </button>
                </div>
              ) : null}
            </div>
          )
        ) : null}

        {/* New blogs section — latest blogs list + promo hero (TeaserPresentational layout) */}
        {newPosts.length > 0 ? (
          <div className="mx-auto flex w-full max-w-[1140px] flex-col items-center justify-center gap-4 bg-[#F8F8F8] px-[26px] py-9 min-[1024px]:h-[656px] min-[1024px]:px-10 min-[1024px]:py-10">
            <div className="flex w-full flex-col items-center justify-center gap-6 min-[768px]:flex-row min-[768px]:items-center min-[1024px]:gap-16">
              {/* Left: heading + scrollable white list */}
              <div className="flex flex-col gap-4">
                {new_blogs_section_title ? (
                  <h2
                    className="text-center text-[22px] font-semibold leading-tight min-[1024px]:text-left min-[1024px]:text-[28px] [font-family:var(--font-poppins),Poppins,sans-serif]"
                    style={{ color: title_text_color }}
                    dangerouslySetInnerHTML={{
                      __html: formatSectionTitle(new_blogs_section_title, "text-primary"),
                    }}
                  />
                ) : null}

                <div
                  onScroll={onNewBlogsScroll}
                  style={{ ["--nb-scroll"]: readMoreColor } as CSSProperties}
                  className="new-blogs-scrollbar flex h-[580px] w-[348px] max-w-full flex-col gap-6 overflow-y-auto p-0 min-[1024px]:h-[518px] min-[1024px]:w-[540px] min-[1024px]:gap-8 min-[1024px]:rounded-xl min-[1024px]:bg-[#FFFFFF] min-[1024px]:p-8"
                >
                  {newPosts.map((post) => newBlogCard(post))}
                </div>
              </div>

              {/* Right: promo hero — asymmetric rounded, bg image, centered text */}
              {new_blogs_hero_image
                ? (() => {
                    const hero = (
                      <div
                        className="flex h-[326px] w-[328px] max-w-full items-center justify-center rounded-[60px_0px_60px_0px] bg-cover bg-center bg-no-repeat text-[#FFFFFF] [font-family:var(--font-poppins),Poppins,sans-serif] min-[1024px]:h-[428px] min-[1024px]:w-[450px]"
                        style={{ backgroundImage: `url(${new_blogs_hero_image})` }}
                        role="img"
                        aria-label={new_blogs_hero_alt || new_blogs_hero_title || ""}
                      >
                        <div className="flex flex-col gap-4 p-6 text-center">
                          {new_blogs_hero_subtitle ? (
                            <p className="text-[28px] italic leading-tight min-[1024px]:text-[36px]">
                              {new_blogs_hero_subtitle}
                            </p>
                          ) : null}
                          {new_blogs_hero_title ? (
                            <p className="text-[48px] font-extrabold leading-[1.05] min-[1024px]:text-[64px]">
                              {new_blogs_hero_title}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    );
                    return new_blogs_hero_url ? (
                      <Link
                        href={new_blogs_hero_url}
                        target={/^https?:\/\//i.test(new_blogs_hero_url) ? "_blank" : undefined}
                      >
                        {hero}
                      </Link>
                    ) : (
                      hero
                    );
                  })()
                : null}
            </div>
          </div>
        ) : null}

        {/* Categories + Featured cards (is_featured by default; category filters; load more) */}
        {categoriesList.length > 0 || featuredPosts.length > 0 ? (
          <div className="mx-auto flex w-full max-w-[1030px] flex-col gap-9">
            {categoriesList.length > 0 ? (
              <div className="flex w-full flex-wrap items-center justify-center gap-[44px] py-[3rem] min-[1024px]:flex-nowrap min-[1024px]:items-start min-[1024px]:justify-start min-[1024px]:overflow-x-auto">
                {categoriesList.map((cat) => {
                  const active = selectedCategoryId === cat.id;
                  const img = cat.background_image_url || cat.icon_url || "";
                  return (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => toggleCategory(cat.id)}
                      className="flex w-[136px] shrink-0 cursor-pointer flex-col items-center justify-center gap-4"
                    >
                      <span
                        className="flex size-[92px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-100 transition-shadow"
                        style={active ? { boxShadow: `0 0 0 3px ${readMoreColor}` } : undefined}
                      >
                        {img ? (
                          <Image
                            src={img}
                            alt={cat.name || ""}
                            width={92}
                            height={92}
                            className="size-full object-cover"
                          />
                        ) : null}
                      </span>
                      <span
                        className="w-[136px] whitespace-normal break-words text-center text-[14px] leading-snug [font-family:var(--font-poppins),Poppins,sans-serif]"
                        style={{ color: active ? readMoreColor : "#171719" }}
                      >
                        {cat.name}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}

            {featuredPosts.length > 0 ? (
              <div className="flex w-full flex-col items-center gap-8">
                <div className="flex w-full flex-wrap items-center justify-center gap-4 min-[768px]:gap-5">
                  {featuredPosts.map((post) =>
                    categoryIdForQuery != null
                      ? newsCard(post)
                      : featuredCard(post),
                  )}
                </div>
                {featuredPosts.length < featuredTotal ? (
                  <button
                    type="button"
                    onClick={() => void loadMoreFeatured()}
                    className="flex cursor-pointer items-center gap-2.5 rounded-[6px] border px-[18px] py-3 text-[16px] font-semibold leading-normal text-[#FFFFFF] transition-opacity hover:opacity-90 disabled:opacity-50 [font-family:var(--font-poppins),Poppins,sans-serif]"
                    style={{ backgroundColor: nav_button_active_bg }}
                  >
                    load more
                    <ArrowUpRight className="size-6" aria-hidden />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}
